import { useState, useEffect, useCallback } from 'react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { Modal } from '@/components/common/Modal'
import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Loader2, Link, UserPlus, AlertTriangle, CheckCircle } from 'lucide-react'
import { TeacherUserLinkDialog } from './TeacherUserLinkDialog'

interface UnlinkedTeacher {
  id: string
  school_id: string
  teacher_code: string | null
  email: string
  full_name: string
  needs_user_linking: boolean
  linking_confidence: number | null
  linking_method: string | null
  created_at: string
}

interface UnlinkedTeachersListResponse {
  teachers: UnlinkedTeacher[]
  total: number
  page: number
  page_size: number
}

interface AutoLinkResponse {
  processed: number
  linked: number
  skipped: number
  requires_review: number
}

export function UnlinkedTeachersList() {
  const [teachers, setTeachers] = useState<UnlinkedTeacher[]>([])
  const [loading, setLoading] = useState(true)
  const [autoLinking, setAutoLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [pageSize] = useState(20)
  const [selectedTeacher, setSelectedTeacher] = useState<UnlinkedTeacher | null>(null)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [confirmAutoLinkOpen, setConfirmAutoLinkOpen] = useState(false)

  const fetchTeachers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('page_size', String(pageSize))
      params.set('include_review_needed', 'true')
      const res = await handleApiRequest<UnlinkedTeachersListResponse>(
        apiClient.get(`/api/v1/admin/teachers/unlinked?${params}`)
      )
      setTeachers(res.teachers)
      setTotal(res.total)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Lehrer konnten nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize])

  useEffect(() => { fetchTeachers() }, [fetchTeachers])

  const handleAutoLinkAll = async () => {
    setConfirmAutoLinkOpen(true)
  }

  const executeAutoLink = async () => {
    setConfirmAutoLinkOpen(false)
    setAutoLinking(true)
    setError(null)
    try {
      const res = await handleApiRequest<AutoLinkResponse>(
        apiClient.post('/api/v1/admin/teachers/auto-link')
      )
      setSuccess(`${res.linked} Lehrer automatisch verknüpft`)
      fetchTeachers()
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Auto-Link fehlgeschlagen' }))
    } finally {
      setAutoLinking(false)
    }
  }

  const handleOpenLinkDialog = (teacher: UnlinkedTeacher, createUser: boolean = false) => {
    setSelectedTeacher(teacher)
    setShowCreateUser(createUser)
    setLinkDialogOpen(true)
  }

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false)
    setSelectedTeacher(null)
    setShowCreateUser(false)
    fetchTeachers()
  }

  const totalPages = Math.ceil(total / pageSize)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {success}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {total} Lehrer ohne Benutzerkonto gefunden
        </div>
        <Button
          onClick={handleAutoLinkAll}
          disabled={autoLinking || teachers.length === 0}
          variant="primary"
        >
          {autoLinking ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Link className="w-4 h-4 mr-2" />
          )}
          Auto-Link starten
        </Button>
      </div>

      {teachers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Alle Lehrer sind mit Benutzerkonten verknüpft
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {teachers.map((teacher) => (
                <tr key={teacher.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {teacher.teacher_code || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {teacher.full_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {teacher.email}
                  </td>
                  <td className="px-4 py-3">
                    {teacher.needs_user_linking ? (
                      <Badge variant="warning">Review nötig</Badge>
                    ) : (
                      <Badge variant="default">Unverknüpft</Badge>
                    )}
                    {teacher.linking_confidence && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({teacher.linking_confidence}% via {teacher.linking_method})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleOpenLinkDialog(teacher, false)}
                        variant="outline"
                        size="sm"
                      >
                        <Link className="w-3 h-3 mr-1" />
                        Verknüpfen
                      </Button>
                      <Button
                        onClick={() => handleOpenLinkDialog(teacher, true)}
                        variant="ghost"
                        size="sm"
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Benutzer erstellen
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            variant="outline"
            size="sm"
          >
            Zurück
          </Button>
          <span className="text-sm text-gray-600">
            Seite {page} von {totalPages}
          </span>
          <Button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            variant="outline"
            size="sm"
          >
            Weiter
          </Button>
        </div>
      )}

      {selectedTeacher && (
        <TeacherUserLinkDialog
          isOpen={linkDialogOpen}
          onClose={() => setLinkDialogOpen(false)}
          teacher={selectedTeacher}
          onSuccess={handleLinkSuccess}
          showCreateUser={showCreateUser}
        />
      )}

      <Modal
        isOpen={confirmAutoLinkOpen}
        onClose={() => setConfirmAutoLinkOpen(false)}
        title="Auto-Link bestätigen"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {total} Lehrer ohne Benutzerkonto gefunden. Automatische Verknüpfung starten?
          </p>
          <p className="text-sm text-gray-500">
            Dies versucht, Lehrer mit bestehenden Benutzerkonten zu verknüpfen, basierend auf E-Mail-Übereinstimmung.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirmAutoLinkOpen(false)} variant="ghost">
              Abbrechen
            </Button>
            <Button onClick={executeAutoLink} variant="primary">
              Starten
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}